import Dayjs from 'dayjs'

import { downloadXlsxSheets } from 'app/pages/Procurement/components/exportUtils'

// Cash register → Excel (user, 2026-10-26: "need to have an option of
// downloading the report in excel"). Built from the ledger rows the screen
// already holds, so the file always says exactly what the page says — no
// second query, no second set of numbers. Four tabs, same as the first report
// Finance was sent by hand: centre summary, cash in hand, every collection,
// and one row per settlement.

const DENOMS = ['500', '200', '100', '50', '20', '10', '5', '2', '1']

const day = (iso) => (iso ? Dayjs(iso).format('DD-MMM-YYYY') : '')
const num = (n) => Math.round(Number(n || 0) * 100) / 100

const routeLabeller = (routes) => {
  const byValue = {}
  ;(routes || []).forEach((r) => {
    byValue[r.value] = r.label
  })
  return (value) => byValue[value] || value || ''
}

const reasonLabel = (r) => r.reason_label || r.reason || ''

const summarySheet = (rows) => {
  const byCentre = {}
  rows.forEach((r) => {
    const c = (byCentre[r.centre] = byCentre[r.centre] || {
      n: 0,
      booked: 0,
      received: 0,
      handN: 0,
      hand: 0,
      oldest: 0,
      routes: {}
    })
    c.n += 1
    c.booked += num(r.booked_amount)
    c.received += num(r.amount_received)
    if (r.status === 'in_hand') {
      c.handN += 1
      c.hand += num(r.amount_received)
      c.oldest = Math.max(c.oldest, r.age_days || 0)
    } else {
      const k = r.deposit?.route || ''
      c.routes[k] = (c.routes[k] || 0) + num(r.amount_received)
    }
  })
  const out = Object.entries(byCentre)
    .sort((a, b) => b[1].hand - a[1].hand)
    .map(([centre, c]) => [
      centre,
      c.n,
      num(c.booked),
      num(c.received),
      num(c.received - c.booked),
      c.handN,
      num(c.hand),
      c.handN ? c.oldest : '',
      num(c.routes.bank),
      num(c.routes.hq),
      num(c.routes.handover),
      num(c.routes.pre_register)
    ])
  const sum = (i) => num(out.reduce((t, r) => t + Number(r[i] || 0), 0))
  out.push([])
  out.push([
    'Total',
    sum(1),
    sum(2),
    sum(3),
    sum(4),
    sum(5),
    sum(6),
    '',
    sum(8),
    sum(9),
    sum(10),
    sum(11)
  ])
  return {
    name: 'Summary by centre',
    headers: [
      'Centre',
      'Collections',
      'Booked (invoice)',
      'Received (cash)',
      'Gap (received − booked)',
      'In hand · count',
      'In hand · amount',
      'Oldest in hand (days)',
      'Deposited in bank',
      'Sent to HQ',
      'Handed to colleague',
      'Banked earlier'
    ],
    rows: out
  }
}

const inHandSheet = (rows) => ({
  name: 'Cash in hand',
  headers: [
    'Centre',
    'Collected on',
    'Days in hand',
    'Student / payer',
    'Parent phone',
    'Mode',
    'Collected by',
    'Booked',
    'Received',
    'Gap reason',
    'Note',
    'Notes counted?'
  ],
  rows: rows
    .filter((r) => r.status === 'in_hand')
    .sort((a, b) => (b.age_days || 0) - (a.age_days || 0))
    .map((r) => [
      r.centre,
      day(r.collected_on),
      r.age_days || 0,
      r.payer,
      r.parent_phone,
      r.mode,
      r.collected_by,
      num(r.booked_amount),
      num(r.amount_received),
      reasonLabel(r),
      r.note || '',
      r.note_count?.counted ? 'Yes' : 'No'
    ])
})

const allSheet = (rows, routeLabel) => ({
  name: 'All collections',
  headers: [
    'Centre',
    'Collected on',
    'Student / payer',
    'Parent phone',
    'Mode',
    'Collected by',
    'Booked',
    'Received',
    'Gap',
    'Gap reason',
    'Note',
    'Status',
    'Days in hand',
    'Settled on',
    'Where it went',
    'Bank / handed to',
    'Settled by',
    'Challan uploaded',
    ...DENOMS.map((d) => `₹${d}`),
    'HQ received on',
    'HQ counted',
    'HQ difference reason',
    'HQ banked on',
    'HQ banked to'
  ],
  rows: [...rows]
    .sort(
      (a, b) =>
        String(a.centre).localeCompare(String(b.centre)) ||
        String(a.collected_on).localeCompare(String(b.collected_on))
    )
    .map((r) => {
      const dep = r.deposit
      const hq = r.hq_receipt
      const counts = r.note_count?.counted ? r.note_count.counts || {} : {}
      return [
        r.centre,
        day(r.collected_on),
        r.payer,
        r.parent_phone,
        r.mode,
        r.collected_by,
        num(r.booked_amount),
        num(r.amount_received),
        num(r.variance),
        reasonLabel(r),
        r.note || '',
        dep ? 'Settled' : 'In hand',
        dep ? '' : r.age_days || 0,
        dep ? day(dep.deposited_on) : '',
        dep ? routeLabel(dep.route) : '',
        dep ? dep.destination || dep.bank || dep.handed_to || '' : '',
        dep ? dep.deposited_by || '' : '',
        dep ? (dep.challans?.length ? 'Yes' : 'No') : '',
        ...DENOMS.map((d) => (counts[d] ? Number(counts[d]) : '')),
        hq ? day(hq.received_on) : '',
        hq ? num(hq.counted_total) : '',
        hq ? hq.reason_label || '' : '',
        hq ? day(hq.banked_on) : '',
        hq ? hq.banked_to || '' : ''
      ]
    })
})

const depositsSheet = (rows, routeLabel) => {
  const byDeposit = {}
  rows.forEach((r) => {
    if (!r.deposit) return
    const d = (byDeposit[r.deposit.uuid] = byDeposit[r.deposit.uuid] || {
      dep: r.deposit,
      centre: r.centre,
      n: 0,
      amount: 0
    })
    d.n += 1
    d.amount += num(r.amount_received)
  })
  return {
    name: 'Deposits',
    headers: [
      'Settled on',
      'Centre',
      'Where it went',
      'Bank / handed to',
      'Settled by',
      'Collections',
      'Amount',
      'Challan uploaded'
    ],
    rows: Object.values(byDeposit)
      .sort((a, b) =>
        String(b.dep.deposited_on).localeCompare(String(a.dep.deposited_on))
      )
      .map((d) => [
        day(d.dep.deposited_on),
        d.centre,
        routeLabel(d.dep.route),
        d.dep.destination || d.dep.bank || d.dep.handed_to || '',
        d.dep.deposited_by || '',
        d.n,
        num(d.amount),
        d.dep.challans?.length ? 'Yes' : 'No'
      ])
  }
}

// `ledger` is the /cash_register/alfred/ledger response; `rows` defaults to
// all of it but the page passes what its filters left on screen.
export const downloadCashRegisterExcel = (ledger, rows, label) => {
  const all = rows || ledger?.results || []
  const routeLabel = routeLabeller(ledger?.routes)
  const name = `Cash Register - ${label || 'All centres'} - ${Dayjs().format(
    'DD MMM YYYY'
  )}`
  downloadXlsxSheets(name, [
    summarySheet(all),
    inHandSheet(all),
    allSheet(all, routeLabel),
    depositsSheet(all, routeLabel)
  ])
}
